import { ClientRequestData, ClientRequestHeaders, ClientResponse } from '../typings';
import { Logger } from './Logger';
import { NetworkRequestError } from '@/src/client/errors/NetworkRequestError';

export class NetworkRequest {
    static request: (
        method: string,
        url: string,
        data?: ClientRequestData | null,
        headers?: ClientRequestHeaders | null,
    ) => Promise<ClientResponse>;

    static get(
        url: string,
        data?: ClientRequestData | null,
        headers?: ClientRequestHeaders | null,
    ): Promise<ClientResponse> {
        return NetworkRequest.request('GET', url, data, headers);
    }

    static post(
        url: string,
        data?: ClientRequestData | null,
        headers?: ClientRequestHeaders | null,
    ): Promise<ClientResponse> {
        return NetworkRequest.request('POST', url, data, headers);
    }

    static put(
        url: string,
        data?: ClientRequestData | null,
        headers?: ClientRequestHeaders | null,
    ): Promise<ClientResponse> {
        return NetworkRequest.request('PUT', url, data, headers);
    }

    static delete(
        url: string,
        data?: ClientRequestData | null,
        headers?: ClientRequestHeaders | null,
    ): Promise<ClientResponse> {
        return NetworkRequest.request('DELETE', url, data, headers);
    }
}

NetworkRequest.request = async (
    method: string,
    url: string,
    data: ClientRequestData | null = null,
    headers: ClientRequestHeaders | undefined | null = null,
): Promise<ClientResponse> => {
    const options: RequestInit = {
        method,
        cache: 'no-cache',
        mode: 'cors',
    };

    if (data) {
        options.body = JSON.stringify(data);
    }

    options.headers = <Headers>new Headers({});
    options.headers.append('Accept', 'application/json, text/plain, */*');
    options.headers.append('Content-Type', 'application/json;charset=UTF-8');

    if (['GET', 'HEAD'].includes(method)) {
        delete options.body;
        delete headers?.['Content-Type'];
    }

    if (headers) {
        Object.keys(headers).forEach((key) => {
            (options.headers as Headers).append?.(key, headers[key]);
        });
    }

    try {
        const response: Response = await fetch(url, options);
        if (!response.ok) {
            throw new NetworkRequestError('Bad status code from server.', url, options);
        }

        const jsonResponse: ClientResponse = await response.json();
        if (jsonResponse?.errors?.length) {
            throw new NetworkRequestError(
                `Errors received from server: ${jsonResponse.errors.join(', ')}`,
                url,
                options,
            );
        }

        if (typeof jsonResponse?.data === 'undefined' || !jsonResponse?.success) {
            throw new NetworkRequestError('Bad response from server.', url, options);
        }

        return Promise.resolve(jsonResponse);
    } catch (responseError) {
        Logger.error('[Sidetalk][Push][NetworkRequest]: Error retrieving response', responseError);

        throw responseError;
    }
};
